/**
 * Admin Customer Management & CRM Store — 100% Live Supabase DB Integration.
 * Aggregates registered users from Supabase DB `profiles` and live purchase history from DB `orders`.
 */

import { supabase } from "@/lib/supabase";
import {
  fetchAdminOrdersFromSupabase,
  type AdminOrderRecord,
} from "@/features/admin/orders/store/admin-orders-store";

export type CustomerTier = "REPEAT" | "NEW" | "BLOCKED";

export interface CustomerSavedAddress {
  id: string;
  type: "SHIPPING" | "BILLING";
  fullName: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  isDefault: boolean;
}

export interface CustomerRecord {
  id: string;
  name: string;
  email: string;
  phone: string;
  tier: CustomerTier;
  isBlocked: boolean;
  blockReason?: string;
  ordersCount: number;
  totalSpent: number;
  avgOrderValue: number;
  joinedDate: string;
  lastOrderDate?: string;
  addresses: CustomerSavedAddress[];
  orders: AdminOrderRecord[];
  wishlistItems: {
    id: string;
    name: string;
    image: string;
    price: number;
  }[];
  notes: {
    timestamp: string;
    user: string;
    text: string;
  }[];
  timeline: {
    timestamp: string;
    event: string;
  }[];
}

/** Structural shape of a `profiles` row as returned by the untyped Supabase client */
interface ProfileRow {
  id?: string;
  email?: string | null;
  full_name?: string | null;
  name?: string | null;
  phone?: string | null;
  is_blocked?: boolean | null;
  block_reason?: string | null;
  created_at?: string | null;
}

const CUSTOMERS_KEY = "verdant_admin_customers_v3";

// Mock customers removed

/** Fetch live customers directly from Supabase DB profiles & orders */
export async function fetchAdminCustomersFromSupabase(): Promise<CustomerRecord[]> {
  const customerMap = new Map<string, CustomerRecord>();

  // Pre-seed map with existing stored customers so notes and local updates are preserved
  const existingStored = getStoredAdminCustomers();
  existingStored.forEach((c) => {
    if (c.email) customerMap.set(c.email.toLowerCase(), c);
  });

  try {
    // 1. Fetch registered user profiles from Supabase DB
    const { data: profiles, error: pError } = await supabase.from("profiles").select("*");

    // 2. Fetch live orders from Supabase DB + stored orders cache
    const dbOrders = await fetchAdminOrdersFromSupabase();
    const orders = dbOrders;

    // Process profiles from DB
    if (!pError && profiles && profiles.length > 0) {
      profiles.forEach((p: ProfileRow) => {
        const userEmail = (p.email || p.id || "").toLowerCase();
        if (!userEmail) return;

        const userOrders = orders.filter(
          (o) =>
            (o.email && o.email.toLowerCase() === userEmail) ||
            (o.customerName && o.customerName.toLowerCase() === (p.full_name || p.name || "").toLowerCase()),
        );

        const ordersCount = userOrders.length;
        const totalSpent = userOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        const avgOrderValue = ordersCount > 0 ? totalSpent / ordersCount : 0;
        const tier: CustomerTier = p.is_blocked ? "BLOCKED" : ordersCount >= 2 ? "REPEAT" : "NEW";

        const prev = customerMap.get(userEmail);

        customerMap.set(userEmail, {
          id: p.id || prev?.id || `cust-${userEmail}`,
          name: p.full_name || p.name || prev?.name || userEmail.split("@")[0],
          email: p.email || userEmail,
          phone: p.phone || userOrders[0]?.phone || prev?.phone || "",
          tier,
          isBlocked: !!p.is_blocked,
          blockReason: p.block_reason || prev?.blockReason || undefined,
          ordersCount: Math.max(ordersCount, prev?.ordersCount || 0),
          totalSpent: Math.max(totalSpent, prev?.totalSpent || 0),
          avgOrderValue: avgOrderValue || prev?.avgOrderValue || 0,
          joinedDate: p.created_at
            ? new Date(p.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : prev?.joinedDate || "Aug 17, 2026",
          lastOrderDate: userOrders[0]?.date || prev?.lastOrderDate,
          orders: userOrders.length > 0 ? userOrders : prev?.orders || [],
          addresses: userOrders[0]?.shippingAddress
            ? [
                {
                  id: `addr-1`,
                  type: "SHIPPING",
                  fullName: userOrders[0].shippingAddress.fullName,
                  address: userOrders[0].shippingAddress.address,
                  city: userOrders[0].shippingAddress.city,
                  state: userOrders[0].shippingAddress.state,
                  pincode: userOrders[0].shippingAddress.pincode,
                  phone: userOrders[0].shippingAddress.phone,
                  isDefault: true,
                },
              ]
            : prev?.addresses || [],
          wishlistItems: prev?.wishlistItems || [],
          notes: prev?.notes || [],
          timeline: prev?.timeline?.length
            ? prev.timeline
            : [
                {
                  timestamp: p.created_at || new Date().toISOString(),
                  event: "Account registered on website.",
                },
              ],
        });
      });
    }

    // Process orders for guest customers or orders not in profile map
    orders.forEach((o) => {
      const userEmail = (o.email || o.customerName || "").toLowerCase();
      if (userEmail) {
        const prev = customerMap.get(userEmail);
        const userOrders = orders.filter(
          (ord) => (ord.email || ord.customerName || "").toLowerCase() === userEmail,
        );
        const ordersCount = userOrders.length;
        const totalSpent = userOrders.reduce((sum, ord) => sum + (ord.total || 0), 0);
        const avgOrderValue = ordersCount > 0 ? totalSpent / ordersCount : 0;
        const tier: CustomerTier = prev?.isBlocked ? "BLOCKED" : ordersCount >= 2 ? "REPEAT" : "NEW";

        customerMap.set(userEmail, {
          id: prev?.id || `cust-${userEmail}`,
          name: o.customerName || prev?.name || userEmail.split("@")[0],
          email: o.email || prev?.email || userEmail,
          phone: o.phone || o.shippingAddress?.phone || prev?.phone || "",
          tier,
          isBlocked: prev?.isBlocked || false,
          blockReason: prev?.blockReason,
          ordersCount: Math.max(ordersCount, prev?.ordersCount || 0),
          totalSpent: Math.max(totalSpent, prev?.totalSpent || 0),
          avgOrderValue: avgOrderValue || prev?.avgOrderValue || 0,
          joinedDate: o.date || prev?.joinedDate || "Aug 17, 2026",
          lastOrderDate: o.date || prev?.lastOrderDate,
          orders: userOrders.length > 0 ? userOrders : prev?.orders || [],
          addresses: o.shippingAddress
            ? [
                {
                  id: `addr-1`,
                  type: "SHIPPING",
                  fullName: o.shippingAddress.fullName,
                  address: o.shippingAddress.address,
                  city: o.shippingAddress.city,
                  state: o.shippingAddress.state,
                  pincode: o.shippingAddress.pincode,
                  phone: o.shippingAddress.phone,
                  isDefault: true,
                },
              ]
            : prev?.addresses || [],
          wishlistItems: prev?.wishlistItems || [],
          notes: prev?.notes || [],
          timeline: prev?.timeline?.length
            ? prev.timeline
            : [
                {
                  timestamp: new Date().toISOString(),
                  event: "First purchase completed.",
                },
              ],
        });
      }
    });

    const resultList = Array.from(customerMap.values());
    if (resultList.length > 0) {
      saveStoredAdminCustomers(resultList);
      return resultList;
    }
  } catch (err) {
    console.error("Failed to fetch customers from DB:", err);
  }

  const stored = getStoredAdminCustomers();
  if (stored.length > 0) {
    return stored;
  }

  return [];
}

/** Get customers from session storage */
export function getStoredAdminCustomers(): CustomerRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CUSTOMERS_KEY);
    if (raw) {
      const parsed: CustomerRecord[] = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error("Failed to read customers from storage", e);
  }
  return [];
}

/** Save customers to session storage */
export function saveStoredAdminCustomers(customers: CustomerRecord[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CUSTOMERS_KEY, JSON.stringify(customers));
    window.dispatchEvent(new Event("customers_updated"));
    try {
      const bc = new BroadcastChannel("customers_sync");
      bc.postMessage({ type: "CUSTOMERS_UPDATED" });
      bc.close();
    } catch {
      // Ignored
    }
  } catch (e) {
    console.error("Failed to save customers to storage", e);
  }
}

/** Toggle block customer status in Supabase DB and local store */
export async function toggleCustomerBlockInStore(
  customerId: string,
  block: boolean,
  reason?: string,
  user: string = "Admin Staff",
): Promise<CustomerRecord[]> {
  const customers = getStoredAdminCustomers();
  let targetEmail = "";
  const updated = customers.map((c) => {
    if (
      c.id === customerId ||
      c.email.toLowerCase() === customerId.toLowerCase() ||
      `cust-${c.email.toLowerCase()}` === customerId.toLowerCase()
    ) {
      targetEmail = c.email;
      return {
        ...c,
        isBlocked: block,
        tier: block
          ? ("BLOCKED" as const)
          : c.ordersCount >= 2
            ? ("REPEAT" as const)
            : ("NEW" as const),
        blockReason: block ? reason : undefined,
        timeline: [
          ...c.timeline,
          {
            timestamp: new Date().toISOString(),
            event: block
              ? `Account Blocked by ${user}. Reason: ${reason || "Policy violation"}`
              : `Account Unblocked by ${user}`,
          },
        ],
      };
    }
    return c;
  });

  // Sync block status to Supabase DB profiles table
  const cleanId = customerId.startsWith("cust-") ? customerId.replace(/^cust-/, "") : customerId;
  const { error } = await supabase
    .from("profiles")
    .update({ is_blocked: block, block_reason: reason })
    .or(`id.eq.${customerId},email.eq.${cleanId},email.eq.${targetEmail}`);

  if (error) {
    console.error("Supabase customer block status update failed:", error);
    throw new Error(`Failed to update customer status in database: ${error.message}`);
  }

  saveStoredAdminCustomers(updated);
  return updated;
}

export { toggleCustomerBlockInStore as toggleBlockCustomerInStore };

/** Synchronously and asynchronously verify if customer is blocked from purchasing */
export function isCustomerBlockedSync(identifier: {
  email?: string;
  phone?: string;
  userId?: string;
}): { isBlocked: boolean; reason?: string } {
  const customers = getStoredAdminCustomers();
  const emailNorm = identifier.email?.toLowerCase().trim();
  const phoneNorm = identifier.phone?.replace(/\D/g, "");
  const userIdNorm = identifier.userId?.trim();

  const found = customers.find((c) => {
    if (!c.isBlocked) return false;
    if (userIdNorm && (c.id === userIdNorm || c.id === `cust-${userIdNorm}`)) return true;
    if (emailNorm && (c.email.toLowerCase() === emailNorm || c.id === `cust-${emailNorm}`))
      return true;
    if (
      phoneNorm &&
      phoneNorm.length >= 10 &&
      c.phone.replace(/\D/g, "").includes(phoneNorm.slice(-10))
    )
      return true;
    return false;
  });

  if (found) {
    return {
      isBlocked: true,
      reason:
        found.blockReason ||
        "Your account has been suspended by administration. Ordering is disabled.",
    };
  }

  return { isBlocked: false };
}

/** Check Supabase database & local cache for blocked status */
export async function checkIsCustomerBlocked(identifier: {
  email?: string;
  phone?: string;
  userId?: string;
}): Promise<{ isBlocked: boolean; reason?: string }> {
  // 1. Instant check against local store
  const localCheck = isCustomerBlockedSync(identifier);
  if (localCheck.isBlocked) return localCheck;

  // 2. Check Supabase DB profiles table
  try {
    const emailNorm = identifier.email?.toLowerCase().trim();
    const userIdNorm = identifier.userId?.trim();

    if (userIdNorm || emailNorm) {
      let query = supabase.from("profiles").select("id, email, is_blocked, block_reason");
      if (userIdNorm && emailNorm) {
        query = query.or(`id.eq.${userIdNorm},email.eq.${emailNorm}`);
      } else if (userIdNorm) {
        query = query.eq("id", userIdNorm);
      } else if (emailNorm) {
        query = query.eq("email", emailNorm);
      }

      const { data } = await query.limit(1).maybeSingle();
      if (data && data.is_blocked) {
        return {
          isBlocked: true,
          reason:
            data.block_reason ||
            "Your account has been suspended by administration. Ordering is disabled.",
        };
      }
    }
  } catch (err) {
    // Database query failover
  }

  return { isBlocked: false };
}

export function addCustomerStaffNoteInStore(
  customerId: string,
  noteText: string,
  user: string = "Admin Staff",
): CustomerRecord[] {
  const customers = getStoredAdminCustomers();
  const updated = customers.map((c) => {
    if (c.id === customerId) {
      return {
        ...c,
        notes: [
          ...c.notes,
          {
            timestamp: new Date().toISOString(),
            user,
            text: noteText,
          },
        ],
      };
    }
    return c;
  });

  saveStoredAdminCustomers(updated);
  return updated;
}
